import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import _ from 'lodash';
import numeral from 'numeral';
import { compose } from 'recompose';
import JsonapiClient from '@holidayextras/jsonapi-client';
import { Message, Segment, Grid, Loader, Icon, Modal, Table, Card, Popup, Label } from 'semantic-ui-react';
import InstagramProfileData from 'dyzio-shared-components/lib/components/instagram-profile-data';
import BrandMentionsTable from 'dyzio-shared-components/lib/components/brand-mentions-table';
import BrandMentionsPostList from 'dyzio-shared-components/lib/components/brand-mentions-post-list';
import TwitchData from 'dyzio-shared-components/lib/components/twitch-data';
import DyzioModal from 'dyzio-shared-components/lib/components/dyzio-modal';
import DyzioDemographics from '../../containers/dyzio-demographics';
import DyzioInstagramEmbed from '../../components/dyzio-instagram-embed2';

import hasUserInfo from '../../hocs/has-user-info';
import hasAWS4Signature from '../../hocs/has-aws4-signature';
import YoutubeProfileDataLarge from '../../components/youtube-profile-data-large';
import FacebookProfileDataLarge from '../../components/facebook-profile-data-large';
import TwitterProfileDataLarge from '../../components/twitter-profile-data-large';
import TiktokProfileDataLarge from '../../components/tiktok-profile-data-large';
import TelegramChannels from './components/telegram-channels';
import { authService } from '../../utils/auth-service';

// const TEAM_DYZIO_ID = 'f14d3931-0e6a-42e8-ad3c-ae2ff07e3d96';
// const DC = 'fdd20559-e10d-4b07-834c-7882c127e82e';
// const JONPLE = 'f4412f47-6788-47ac-84c9-dae56067a8c1';
// const HERE_AND_NOW = '15e0ffdb-bd62-4eaf-b8ba-fb242ebc6fd6';

// const DEMOGRAPHICS_ALLOWED_ORGS = [TEAM_DYZIO_ID, HERE_AND_NOW, JONPLE, DC];

const INSTAGRAM_POST_URL = 'https://www.instagram.com/p';

const brandMentionsLoadingStyle = {
  minHeight: 90,
  marginTop: 30,
};

const emptyDataStyle = {
  marginTop: 14,
};

const overflowEllipsis = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: "600px",
};

const sf = num => {
  if (num) return (num > 1000 ? numeral(num).format('0.0a') : num);
  return '-';
};

class InfluencerProfileStats extends React.Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      missingAPILink: false,
      error: false,
      statistics: {},
      youtubeDemographicsModalOpen: false,
      data: {
        youtube: null,
        instagram: null,
        twitter: null,
      },
      history: null,
      loadingHistory: true,
      brandMentionsLoading: true,
      brandMentionsData: null,
      brandMentionsStatus: null,
    };

    this.restClient = axios.create({
      timeout: 5000,
      headers: {
        Authorization: `Bearer ${authService.getToken()}`,
      },
    });

    this.jsonClient = new JsonapiClient(process.env.REACT_APP_API_BASE_URL, {
      header: {
        Authorization: `Bearer ${authService.getToken()}`,
      },
    });

    axiosRetry(this.restClient, {
      delay: 100,
      retries: 3,
    });
  }

  componentDidMount() {
    const profile = this.props.influencer;
    const statistics = profile.statistics;
    const link = profile.influencerApiLink;

    if (link && link.url) {
      this.restClient.get(link.url).then(response => {
        const { influencer } = response.data;
        const youtube = influencer.aggregateYoutubeChannelStatistics;
        const instagram = influencer.instagramStatistics;
        const twitter = influencer.twitterStatistics;
        const facebook = influencer.facebookStatistics;

        const data = {
          youtube,
          instagram,
          twitter,
          facebook,
          apiInfluencer: influencer,
        };

        return this.setState({
          statistics,
          data,
          loading: false,
        }, this.loadCampaignHistory);
      }).catch(() => {
        this.setState({
          error: true,
          loading: false,
        }, this.loadCampaignHistory);
      });
    } else {
      this.setState({
        missingAPILink: true,
        loading: false,
      }, this.loadCampaignHistory);
    }
  }

  toggleYoutubeDemographicsModalState = () => {
    const { youtubeDemographicsModalOpen } = this.state;
    this.setState({
      youtubeDemographicsModalOpen: !youtubeDemographicsModalOpen,
    });
  }

  toggleFacebookCountryDemographicsModalState = () => {
    const { facebookCountryDemographicsModalOpen } = this.state;
    this.setState({
      facebookCountryDemographicsModalOpen: !facebookCountryDemographicsModalOpen,
    });
  }

  toggleFacebookCityDemographicsModalState = () => {
    const { facebookCityDemographicsModalOpen } = this.state;
    this.setState({
      facebookCityDemographicsModalOpen: !facebookCityDemographicsModalOpen,
    });
  }

  togglePageMetricModalState = () => {
    const { pageMetricModalOpen } = this.state;
    this.setState({
      pageMetricModalOpen: !pageMetricModalOpen,
    });
  }

  loadCampaignHistory = () => {
    const profile = this.props.influencer;
    const { id } = profile;

    // load brand mentions in parallel
    this.loadBrandMentions();

    this.restClient.get(`${process.env.REACT_APP_INFLUENCER_DATA_API}/influencer/campaings`, {
      params: { id },
    }).then(response => {
      const { data } = response;
      return this.setState({
        history: data,
        loadingHistory: false,
      });
    }).catch(() => {
      this.setState({
        history: null,
        loadingHistory: false,
      });
    });
  }

  loadBrandMentions = () => {
    const profile = this.props.influencer;
    const { instagramId, id } = profile;

    if (instagramId) {
      const brandURLBase = `https://9uvsiskwi2.execute-api.eu-west-1.amazonaws.com/production`;
      const url = `${brandURLBase}/instagram/posts/${id}?rapid=true`;
      return axios.get(url).then(res => {
        const posts = _.get(res, 'data.data.formattedNodes')
        const adPosts = _.filter(posts, post => post.isAd);
        const sortByDateAdPosts = _.reverse(_.sortBy(adPosts, 'publishedDate'));
        console.log('adPosts', adPosts);
        this.setState({
          brandMentionsData: _.take(adPosts,12),
          brandMentionsLoading: false,
        })
      }).catch( e => {
        console.log(e);
        this.setState({
          brandMentionsLoading: false,
        });
      });
    }
    this.setState({
      brandMentionsLoading: false,
      brandMentionsData: null,
    });

    return null;
  }

  handleBrandMentionsRowClick = item => {
    console.log(item);
    this.setState({
      brandCollaborationData: item,
      showBrandCollaborationModal: true,
    });
  }

  popupToggleHandler = () => {
    this.setState({
      showBrandCollaborationModal: false,
    });
  }

  getBrandCollabModalSize = brandCollaborationData => {
    if (!brandCollaborationData) return null;
    const postCount = brandCollaborationData.engagementFigures.relatedPosts.length;
    let size;
    switch (postCount) {
    case 1:
      size = 'tiny';
      break;
    case 2:
      size = 'small';
      break;
    default:
      size = 'large';
    }
    return size;
  }

  render() {
    const {
      data,
      loading,
      error,
      statistics,
      missingAPILink,
      youtubeDemographicsModalOpen,
      facebookCountryDemographicsModalOpen,
      facebookCityDemographicsModalOpen,
      pageMetricModalOpen,
      brandMentionsData,
      brandMentionsStatus,
      brandMentionsLoading,
      showBrandCollaborationModal,
      brandCollaborationData,
    } = this.state;

    const brandCollabModalSize = this.getBrandCollabModalSize(brandCollaborationData);

    const { influencer, userInfo: { organisationId }, organisation } = this.props;
    const isYouTubeCompliance = (organisationId === 'ea9ed959-9566-4851-b69c-55b699832cff');
    const displayAuthorisedData = _.get(organisation, 'displayAuthorisedData', false);
    // const canOrgDisplayDemographics = _.includes(DEMOGRAPHICS_ALLOWED_ORGS, organisationId);

    // const canViewData = dataPermission && dataPermission.requestStatus === REQUEST_STATUS_APPROVED;
    // const canViewInstagram = canViewData && _.find(dataPermission.channels, ['channel', 'instagram']);
    // const canViewFacebook = canViewData && _.find(dataPermission.channels, ['channel', 'facebook']);
    // const canViewYoutube = canViewData && _.find(dataPermission.channels, ['channel', 'youtube']);

    const canViewData = true;
    const canViewInstagram = _.has(influencer, 'instagram');
    const canViewFacebook = _.has(influencer, 'facebook');
    const canViewTwitter = _.has(influencer, 'twitter');
    const canViewYoutube = _.has(influencer, 'youtube');
    const canViewTwitch = _.has(influencer, 'twitch');
    const canViewTiktok = _.has(influencer, 'tiktok');

    const {
      facebook,
      facebookData,
      facebookVerified,
      countHistory,
    } = influencer;

    const hasDemographicsData = _.has(influencer, 'instagramData.audienceDemographics.data');

    const IG_CONTENT_TYPES = ['REELS', 'CAROUSEL', 'IMAGE', 'VIDEO', 'IGTV'];

    let mappedLocations = null;

    if (influencer.instagramLocations) {
      const groupedLocations = _.groupBy(influencer.instagramLocations, 'name');
      mappedLocations = _.map(groupedLocations, (v, k) => ({ name: k, id: _.head(v).id, count: v.length }));
    }
    return (
      <div className="page-content">

        {
          !canViewData &&
          <Message
            info
            content={'No permissions. Please use "Send ADR request" button to get permissions for viewing data.'}
          />
        }

        {
          canViewInstagram &&
          <InstagramProfileData
            displayAuthorisedData={displayAuthorisedData}
            loading={loading}
            influencer={influencer}
            stats={statistics}
            data={data.instagram}
            error={error}
            organisationId={organisationId}
          />
        }

        {
          canViewInstagram && (!hasDemographicsData || !displayAuthorisedData) &&
          <DyzioDemographics influencer={influencer} organisationId={organisationId} />
        }

        {
          canViewInstagram &&
          <div>
            <Segment>
              <Grid columns={2}>
                <Grid.Row>
                  <Grid.Column width={4}>
                    <h3>Brand Collaborations</h3>
                    <br />
                    <p>dyzio tracks any #Ads, Paid Partnerships or
                  sponsored content your influencer posts on their Instagram feed.</p>
                  </Grid.Column>
                  <Grid.Column width={12}>
                    {
                      brandMentionsLoading &&
                      <Loader
                        active
                        inline="centered"
                        content="Loading brand mentions data"
                        style={brandMentionsLoadingStyle}
                      />
                    }
                    {
                      !brandMentionsLoading && _.isEmpty(brandMentionsData) && (
                        <Message icon info style={emptyDataStyle}>
                          <Icon name="circle outline" />
                          <Message.Content>
                            <Message.Header>No brand mentions yet</Message.Header>
                              No brand mentions were found in their last 50 instagram posts
                          </Message.Content>
                        </Message>
                      )
                    }
                    {
                      brandMentionsData && brandMentionsData.length > 0 &&
                        <div style={{display: 'flex', flexDirection:'column', flexWrap: 'wrap'}}>
                          {_.map(brandMentionsData, brandMention => 
                            <Card style={{display: 'flex', flexDirection: 'row', width: "90%"}}>
                              <a
                                  style={{margin: 5}}
                                  title="open on Instagram"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href={`${INSTAGRAM_POST_URL}/${brandMention.shortcode}`}
                                >
                                  <DyzioInstagramEmbed
                                    assetId={brandMention.shortcode}
                                    brandMention
                                  />
                                </a>
                                <Table singleLine style={{margin: 0}}>
                                  <Table.Row>
                                    <Table.Cell>
                                      <Icon name="like outline" /> { brandMention.likeCount < 0 ? 'hidden' : sf(brandMention.likeCount) }
                                    </Table.Cell>
                                    <Table.Cell>
                                      <Icon name="comments outline" /> { sf(brandMention.commentCount) }
                                    </Table.Cell>
                                    <Table.Cell>
                                      <Icon name="eye" /> { sf(brandMention.viewCount) }
                                    </Table.Cell>
                                  </Table.Row>
                                  <Table.Row>
                                    <Table.Cell colSpan="3">
                                      <div style={overflowEllipsis}>
                                        {brandMention.text || '-' }
                                      </div>
                                    </Table.Cell>
                                  </Table.Row>
                                  { brandMention.sponsor && <Table.Row>
                                    <Table.Cell colSpan="3">
                                      <Popup
                                        content={brandMention.sponsor}
                                        trigger={
                                          <span>
                                            <Icon name="money" color="pink" />
                                            <Label>{brandMention.sponsor.username}</Label>
                                          </span>
                                        }
                                      />
                                    </Table.Cell>
                                  </Table.Row>}
                                  <Table.Row>
                                    <Table.Cell colSpan="3">
                                      <Popup
                                        content={_.join(brandMention.hashtags, ', ')}
                                        trigger={
                                          <span>
                                            { _.map(_.take(_.uniq(brandMention.mentions, 5)), m => (<Label>{_.toLower(m)}</Label>)) }
                                            { _.map(_.take(_.uniq(brandMention.hashtags, 5)), h => (<Label>{_.toLower(h)}</Label>)) }
                                          </span>
                                        }
                                      />
                                    </Table.Cell>
                                  </Table.Row>
                                </Table>
                            </Card>
                          )}
                        </div>
                    }
                    {
                      !brandMentionsLoading && !brandMentionsData && (
                        <Message icon info style={emptyDataStyle}>
                          <Icon name="circle outline" />
                          <Message.Content>
                            <Message.Header>No brand mentions yet</Message.Header>
                              No brand mentions were found in their last 50 instagram posts
                          </Message.Content>
                        </Message>
                      )
                    }
                  </Grid.Column>
                </Grid.Row>
              </Grid>


            </Segment>

            <Segment>

              <Grid columns={3}>

                <Grid.Row>
                  <Grid.Column width={5}>
                    <h3>Influencer Locations</h3>
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Location</Table.HeaderCell>
                          <Table.HeaderCell>Count</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {
                          mappedLocations ?
                            _.map(_.orderBy(mappedLocations, ['count'], ['desc']), h => (
                              <Table.Row>
                                <Table.Cell>
                                  <a target="_blank" href={`https://www.instagram.com/explore/locations/${h.id}/`}>{h.name}</a>
                                </Table.Cell>
                                <Table.Cell>
                                  {h.count}
                                </Table.Cell>
                              </Table.Row>
                            ))
                            : <Table.Row>
                              <Table.Cell>
                                <p>no locations tagged...</p>
                              </Table.Cell>
                            </Table.Row>
                        }
                      </Table.Body>
                    </Table>
                  </Grid.Column>

                  <Grid.Column width={6}>
                    <h3>Content Analysis (Last 12 posts)</h3>
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>content type</Table.HeaderCell>
                          <Table.HeaderCell>count</Table.HeaderCell>
                          <Table.HeaderCell>Eng. Rate</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {
                          influencer.instagramBenchmarks ?
                            _.map(IG_CONTENT_TYPES, ct => (
                              <Table.Row>
                                <Table.Cell>{ct}</Table.Cell>
                                <Table.Cell>
                                  {_.get(influencer.instagramBenchmarks[ct], 'count', 0)}
                                </Table.Cell>
                                <Table.Cell>
                                  {_.get(influencer.instagramBenchmarks[ct], 'engagementRate', '-')}%
                                </Table.Cell>
                              </Table.Row>
                            ))
                            : <Table.Row>
                              <Table.Cell>
                                <p>not scanned yet...</p>
                              </Table.Cell>
                            </Table.Row>
                        }
                      </Table.Body>
                    </Table>
                  </Grid.Column>
                  <Grid.Column width={5}>

                    <h3>Hashtags (Top 10)</h3>
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Hashtag</Table.HeaderCell>
                          <Table.HeaderCell>Count</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {
                          influencer.instagramHashtags ?
                            _.map(_.take(influencer.instagramHashtags, 10), h => (
                              <Table.Row>
                                <Table.Cell>
                                  {h.hashtag}
                                </Table.Cell>
                                <Table.Cell>
                                  {h.count}
                                </Table.Cell>
                              </Table.Row>
                            ))
                            : <Table.Row>
                              <Table.Cell>
                                <p>not scanned yet...</p>
                              </Table.Cell>
                            </Table.Row>
                        }
                      </Table.Body>
                    </Table>
                  </Grid.Column>

                </Grid.Row>
              </Grid>
            </Segment>
          </div>
        }

        {
          canViewYoutube &&
          <YoutubeProfileDataLarge
            displayAuthorisedData={displayAuthorisedData}
            loading={loading}
            influencer={influencer}
            apiInfluencer={data.apiInfluencer}
            data={data.youtube}
            error={error}
            missingAPILink={missingAPILink}
            isYouTubeCompliance={isYouTubeCompliance}
            youtubeDemographicsModalOpen={youtubeDemographicsModalOpen}
            toggleYoutubeDemographicsModalState={this.toggleYoutubeDemographicsModalState}
          />
        }

        {
          canViewFacebook &&
          <FacebookProfileDataLarge
            displayAuthorisedData={displayAuthorisedData}
            {...facebookData}
            handle={facebook}
            facebookVerified={facebookVerified}
            countHistory={countHistory}
            facebookCountryDemographicsModalOpen={facebookCountryDemographicsModalOpen}
            toggleFacebookCountryDemographicsModalState={this.toggleFacebookCountryDemographicsModalState}
            facebookCityDemographicsModalOpen={facebookCityDemographicsModalOpen}
            toggleFacebookCityDemographicsModalState={this.toggleFacebookCityDemographicsModalState}
            onFullPageMetrics={this.togglePageMetricModalState}
            pageMetricModalOpen={pageMetricModalOpen}
          />
        }

        {
          canViewTwitter &&
          <TwitterProfileDataLarge
            influencer={influencer}
            countHistory={countHistory}
          />
        }

        {
          canViewTiktok &&
          <TiktokProfileDataLarge
            influencer={influencer}
            countHistory={countHistory}
          />
        }

        {
          canViewTwitch &&
          <TwitchData influencer={influencer} />
        }

        <TelegramChannels
          influencer={influencer}
          channels={influencer.telegramChannels}
          user={influencer.telegramUser}
        />

        {
          showBrandCollaborationModal &&
          <DyzioModal
            onClose={this.popupToggleHandler}
            headerText="Brand Collaboration"
            size={brandCollabModalSize}
          >
            <Modal.Content>
              <BrandMentionsPostList
                brandName={brandCollaborationData.brand}
                brandId={brandCollaborationData.id}
                brandHandle={brandCollaborationData.handle}
                brandProfilePic={brandCollaborationData.profilePicUrl}
                shortcodes={brandCollaborationData.engagementFigures.relatedPosts}
              />
            </Modal.Content>
          </DyzioModal>
        }

      </div>
    );
  }
}

InfluencerProfileStats.propTypes = {
  organisation: PropTypes.object.isRequired,
  influencer: PropTypes.object.isRequired,
  userInfo: PropTypes.object.isRequired,
  signAWS4Request: PropTypes.func.isRequired,
};

export default compose(
  hasAWS4Signature,
  hasUserInfo,
)(InfluencerProfileStats);



/** WEBPACK FOOTER **
 ** ./src/components/influencer-profile-stats/index.js
 **/